/**
 * use-text-size.ts — hook canonico para o controle "Tamanho do texto".
 * Fase 1 do plano de acessibilidade tipografica (regua Fase 0 22/07/2026).
 *
 * 4 niveis, gravados na variavel --dynamic-type do <html> em PORCENTAGEM.
 * Porcentagem na raiz resolve contra o tamanho de fonte que o proprio
 * navegador/sistema ja usa — entao respeitamos a preferencia de acessibilidade
 * do usuario (quem aumentou a fonte do celular ja recebe letra grande no
 * Padrao) e ainda escalamos por cima:
 * - 'default' (100%)   — respeita o sistema (nao forca 16px)
 * - 'large'   (112.5%)
 * - 'larger'  (125%)
 * - 'max'     (137.5%)
 *
 * Os niveis sao proporcionais, entao sempre CRESCEM em relacao ao Padrao,
 * qualquer que seja o tamanho base do aparelho.
 *
 * O gancho `html { font-size: var(--dynamic-type, 16px) }` ja existe em theme.css.
 * Como todos os tokens tipograficos estao em rem, mudar --dynamic-type escala
 * TODAS as telas proporcionalmente sem tocar em nenhuma delas.
 *
 * Persiste em localStorage florasegura:textsize.
 */
import { useState } from 'react'

export type TextSize = 'default' | 'large' | 'larger' | 'max'

const STORAGE_KEY = 'florasegura:textsize'

/**
 * Mapa nivel -> porcentagem aplicada no font-size do root.
 * 100% = tamanho que o navegador/sistema ja usa (respeita acessibilidade).
 * Os demais crescem proporcionalmente por cima desse base.
 */
const SIZE_PCT: Record<TextSize, string> = {
  default: '100%',
  large: '112.5%',
  larger: '125%',
  max: '137.5%',
}

function readStoredTextSize(): TextSize {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v === 'default' || v === 'large' || v === 'larger' || v === 'max') return v
  } catch { /* localStorage indisponivel */ }
  return 'default'
}

function applyTextSize(size: TextSize) {
  const root = document.documentElement
  root.style.setProperty('--dynamic-type', SIZE_PCT[size])
}

/**
 * Hook canonico de tamanho de texto. Retorna estado atual + setter.
 */
export function useTextSize() {
  const [textSize, setTextSizeState] = useState<TextSize>(() => {
    const stored = readStoredTextSize()
    applyTextSize(stored)
    return stored
  })

  function setTextSize(s: TextSize) {
    setTextSizeState(s)
    try { localStorage.setItem(STORAGE_KEY, s) } catch { /* */ }
    applyTextSize(s)
  }

  return { textSize, setTextSize }
}

/**
 * Aplica o tamanho persistido o mais cedo possivel (chame em main.tsx).
 * Garante que o app inicia ja com --dynamic-type correto, evitando flash.
 */
export function applyStoredTextSizeImmediately() {
  applyTextSize(readStoredTextSize())
}
